import { useEffect, useMemo, useRef, useState } from "react";
import { PlayCircle, ExternalLink } from "lucide-react";

/**
 * Parse a video URL into an embed descriptor. Anything unrecognized
 * returns kind='link' so we render a click-through card — no site is a
 * hard failure.
 *
 * Supported iframe embeds:
 *   - YouTube (youtu.be, youtube.com/watch, /shorts, /embed, /live)
 *   - Vimeo (vimeo.com/{id}, player.vimeo.com/video/{id})
 *   - Reddit (redditmedia.com iframe of any reddit.com post)
 *   - TikTok (tiktok.com/@handle/video/{id})
 *   - Instagram (instagram.com/p/{shortcode}, /reel/{shortcode})
 *   - Twitch clips (clips.twitch.tv, twitch.tv/{ch}/clip/{slug})
 *   - Streamable (streamable.com/{id})
 *   - Kick (kick.com/{ch}?clip=)
 *   - Dailymotion (dailymotion.com/video/{id}, dai.ly/{id})
 *
 * Native <video> playback:
 *   - Direct file URLs ending in .mp4/.webm/.mov
 *
 * Click-through card (embed blocked by provider — includes NFL.com, ESPN, X/Twitter, etc.):
 *   - Everything else.
 */
export function parseVideoUrl(raw) {
  const url = (raw || "").trim();
  if (!url) return null;
  let u;
  try {
    u = new URL(url.startsWith("http") ? url : `https://${url}`);
  } catch {
    return null;
  }
  const host = u.hostname.replace(/^www\./, "").toLowerCase();
  const path = u.pathname;
  const segs = path.split("/").filter(Boolean);

  // ---------- YouTube ----------
  if (host === "youtu.be") {
    const id = segs[0];
    if (id) return { kind: "youtube", id, src: `https://www.youtube-nocookie.com/embed/${id}` };
  }
  if (host === "youtube.com" || host === "m.youtube.com" || host === "youtube-nocookie.com") {
    const v = u.searchParams.get("v");
    if (v) return { kind: "youtube", id: v, src: `https://www.youtube-nocookie.com/embed/${v}` };
    if (segs[0] === "shorts" && segs[1]) return { kind: "youtube", id: segs[1], src: `https://www.youtube-nocookie.com/embed/${segs[1]}` };
    if (segs[0] === "embed" && segs[1]) return { kind: "youtube", id: segs[1], src: `https://www.youtube-nocookie.com/embed/${segs[1]}` };
    if (segs[0] === "live" && segs[1]) return { kind: "youtube", id: segs[1], src: `https://www.youtube-nocookie.com/embed/${segs[1]}` };
  }

  // ---------- Vimeo ----------
  if (host === "vimeo.com" || host === "player.vimeo.com") {
    const id = segs[0] === "video" ? segs[1] : segs[0];
    if (id && /^\d+$/.test(id)) return { kind: "vimeo", id, src: `https://player.vimeo.com/video/${id}` };
  }

  // ---------- Reddit ----------
  // Any reddit.com or v.redd.it link → redditmedia.com iframe of the post page.
  if (host === "reddit.com" || host === "old.reddit.com" || host === "new.reddit.com") {
    if (segs[0] === "r" && segs[2] === "comments" && segs[3]) {
      const embedPath = `/${segs.slice(0, 5).join("/")}`;
      const src = `https://www.redditmedia.com${embedPath}/?embed=true&showedits=false&showtitle=true&showmedia=true&theme=dark`;
      return { kind: "reddit", src };
    }
  }

  // ---------- TikTok ----------
  if (host === "tiktok.com" || host === "vm.tiktok.com") {
    // /@handle/video/{id}
    const videoIdx = segs.findIndex((s) => s === "video");
    if (videoIdx >= 0 && segs[videoIdx + 1]) {
      const id = segs[videoIdx + 1];
      return { kind: "tiktok", id, src: `https://www.tiktok.com/embed/v2/${id}` };
    }
    // vm.tiktok.com/XXXX — short link, best-effort passthrough
    if (host === "vm.tiktok.com" && segs[0]) {
      return { kind: "tiktok", src: `https://www.tiktok.com/embed/v2/${segs[0]}` };
    }
  }

  // ---------- Instagram ----------
  if (host === "instagram.com") {
    if ((segs[0] === "p" || segs[0] === "reel" || segs[0] === "tv") && segs[1]) {
      return { kind: "instagram", id: segs[1], src: `https://www.instagram.com/${segs[0]}/${segs[1]}/embed` };
    }
  }

  // ---------- Twitch ----------
  if (host === "clips.twitch.tv" && segs[0]) {
    // Parent domain is required; use window.location.host at render time.
    return { kind: "twitch-clip", id: segs[0] };
  }
  if (host === "twitch.tv" && segs[1] === "clip" && segs[2]) {
    return { kind: "twitch-clip", id: segs[2] };
  }

  // ---------- Streamable ----------
  if (host === "streamable.com" && segs[0]) {
    return { kind: "streamable", id: segs[0], src: `https://streamable.com/e/${segs[0]}` };
  }

  // ---------- Kick ----------
  if (host === "kick.com" && u.searchParams.get("clip")) {
    const clip = u.searchParams.get("clip");
    return { kind: "kick", id: clip, src: `https://kick.com/${segs[0]}/clips/${clip}` };
  }

  // ---------- Dailymotion ----------
  if (host === "dai.ly" && segs[0]) {
    return { kind: "dailymotion", id: segs[0], src: `https://www.dailymotion.com/embed/video/${segs[0]}` };
  }
  if (host === "dailymotion.com" && segs[0] === "video" && segs[1]) {
    return { kind: "dailymotion", id: segs[1], src: `https://www.dailymotion.com/embed/video/${segs[1]}` };
  }

  // ---------- Direct file ----------
  if (/\.(mp4|webm|mov|m4v)(\?|$)/i.test(u.pathname)) return { kind: "file", src: url };
  if (/\.m3u8(\?|$)/i.test(u.pathname)) return { kind: "hls", src: url };

  // Fallback — anything else (NFL.com video pages, ESPN, X/Twitter, Facebook, etc.).
  return { kind: "link", src: url, host };
}

// Which iframe kinds accept a `?autoplay=1&mute=1` param.
const AUTOPLAY_QUERY = {
  youtube: "?autoplay=1&mute=1&playsinline=1&rel=0",
  vimeo: "?autoplay=1&muted=1&playsinline=1",
  dailymotion: "?autoplay=1&mute=1",
  streamable: "?autoplay=1&muted=1",
};

function withAutoplay(kind, src) {
  const q = AUTOPLAY_QUERY[kind];
  if (!q) return src;
  return src + (src.includes("?") ? q.replace("?", "&") : q);
}

/** IntersectionObserver hook — returns [ref, inView]. Uses a synchronous
 * rect check on mount so short articles where the video is already visible
 * don't flash the "Scroll to load" placeholder for a frame.
 */
function useInView(threshold = 0.4) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const node = ref.current;
    if (!node) return undefined;
    // Synchronous first-paint check.
    const rect = node.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const visible = rect.top < vh * (1 - threshold) && rect.bottom > vh * threshold;
    if (visible) setInView(true);
    const io = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting && entry.intersectionRatio >= threshold),
      { threshold: [0, threshold, 1] },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, inView];
}

export default function VideoEmbed({ url, caption, credit, className = "", testid, autoplay = true }) {
  const parsed = useMemo(() => parseVideoUrl(url), [url]);
  const [wrapperRef, inView] = useInView(0.35);
  // For iframes we lazy-mount the frame the first time it comes into view. Once
  // mounted we keep it — pausing/resuming iframes requires the vendor's JS SDK
  // which is a lot of weight for a small UX gain. Native <video> DOES pause on scroll.
  const [ignited, setIgnited] = useState(false);
  useEffect(() => {
    if (inView && autoplay) setIgnited(true);
  }, [inView, autoplay]);

  const videoRef = useRef(null);
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !autoplay) return;
    if (inView) {
      const p = el.play?.();
      if (p && typeof p.catch === "function") p.catch(() => { /* autoplay blocked — user tap required */ });
    } else {
      el.pause?.();
    }
  }, [inView, autoplay]);

  if (!parsed) return null;

  const shell =
    "relative w-full aspect-video bg-black border-2 border-titans-blue/70 shadow-[0_14px_38px_-18px_rgba(0,0,0,0.7)] overflow-hidden";

  // Providers that use portrait aspect by default
  const portraitShell =
    "relative w-full max-w-[420px] mx-auto aspect-[9/16] bg-black border-2 border-titans-blue/70 shadow-[0_14px_38px_-18px_rgba(0,0,0,0.7)] overflow-hidden";

  let player = null;
  let outerShell = shell;

  if (parsed.kind === "file") {
    player = (
      <video
        ref={videoRef}
        src={parsed.src}
        controls
        muted={autoplay}
        loop={autoplay}
        playsInline
        preload="metadata"
        className="absolute inset-0 w-full h-full object-contain bg-black"
      />
    );
  } else if (parsed.kind === "hls" || parsed.kind === "link") {
    player = <LinkCard href={parsed.src} host={parsed.host} />;
  } else if (parsed.kind === "twitch-clip") {
    // Twitch clips require the parent domain param.
    const parent = typeof window !== "undefined" ? window.location.hostname : "thetitanstandard.com";
    if (!autoplay) {
      player = <ProviderIframe src={`https://clips.twitch.tv/embed?clip=${parsed.id}&parent=${parent}`} title={caption || "Twitch clip"} />;
    } else {
      const src = ignited
        ? `https://clips.twitch.tv/embed?clip=${parsed.id}&parent=${parent}&autoplay=true&muted=true`
        : null;
      player = src ? <ProviderIframe src={src} title={caption || "Twitch clip"} /> : <IframePlaceholder />;
    }
  } else {
    // YouTube / Vimeo / Reddit / TikTok / Instagram / Streamable / Kick / Dailymotion
    if (parsed.kind === "tiktok" || parsed.kind === "instagram") outerShell = portraitShell;
    if (!autoplay) {
      player = <ProviderIframe src={parsed.src} title={caption || "Video"} />;
    } else {
      const finalSrc = ignited ? withAutoplay(parsed.kind, parsed.src) : null;
      player = finalSrc ? <ProviderIframe src={finalSrc} title={caption || "Video"} /> : <IframePlaceholder />;
    }
  }

  return (
    <figure
      className={`my-8 ${className}`}
      data-testid={testid}
      ref={wrapperRef}
      data-video-kind={parsed.kind}
      data-video-inview={inView ? "1" : "0"}
    >
      <div className={outerShell}>{player}</div>
      {(caption || credit) && (
        <figcaption className="article-figcaption">
          {caption && <span>{caption}</span>}
          {credit && <span className="ts-photo-credit">Mandatory Credit: {credit}</span>}
        </figcaption>
      )}
    </figure>
  );
}

function ProviderIframe({ src, title }) {
  return (
    <iframe
      src={src}
      title={title}
      loading="lazy"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      referrerPolicy="strict-origin-when-cross-origin"
      className="absolute inset-0 w-full h-full"
      sandbox="allow-scripts allow-same-origin allow-popups allow-presentation allow-forms"
    />
  );
}

function IframePlaceholder() {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-titans-navy to-titans-void">
      <PlayCircle size={54} className="text-titans-red mb-3 animate-pulse" />
      <div className="font-cond uppercase tracking-[0.3em] text-xs text-titans-blue">
        Scroll to load
      </div>
    </div>
  );
}

function LinkCard({ href, host }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="absolute inset-0 flex flex-col items-center justify-center text-white bg-gradient-to-br from-titans-navy to-titans-void hover:from-titans-blue/40 hover:to-titans-navy transition-colors"
    >
      <PlayCircle size={54} className="text-titans-red mb-3" />
      <div className="font-cond uppercase tracking-[0.3em] text-xs text-titans-blue mb-1">
        Watch video
      </div>
      <div className="font-display uppercase tracking-wider text-sm text-white max-w-[85%] truncate flex items-center gap-2">
        {host || href.replace(/^https?:\/\//, "").split("/")[0]}
        <ExternalLink size={12} />
      </div>
      <div className="text-[10px] text-titans-muted mt-2 max-w-[85%] truncate">{href}</div>
    </a>
  );
}

